import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, KeyRound, Link2, Mail, RefreshCw, Share2, ShieldAlert } from 'lucide-react';
import { inviteFor, invitedAgo, RESEND_INVITE_LABEL, inviteLinkExpiry, inviteLinkExpiryExact, inviteLinkWarning, type InviteLink, type InviteLinkEvent, type MemberInvitation } from '@tradeoutlet/shared';
import { Button, Input, Modal, ask } from '@/components/ui';
import { HoverBubble } from '@/components/ui/HoverBubble';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useCan } from '@/lib/permissions';
import { useUi } from '@/lib/store';
import { queryClient } from '@/lib/queryClient';
import { useResendInvite } from './EditUser';

/**
 * t172: an invitation's sign-up link for the admin to copy or share (the email still goes out too).
 * The link is a password-setting key: it is only fetched on demand (audited), never put in a URL, and never logged.
 */

/** Share sheet on phones (Web Share API); everywhere else, or if sharing isn't possible, copy. */
export async function shareOrCopy(link: InviteLink, workspace = 'The Hub'): Promise<InviteLinkEvent | null> {
  const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void>; canShare?: (d: ShareData) => boolean };
  const data: ShareData = { title: `Your invitation to ${workspace}`, text: `Set up your ${workspace} account:`, url: link.url };
  const phone = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  if (phone && nav.share && (!nav.canShare || nav.canShare(data))) {
    try { await nav.share(data); return 'shared'; }
    catch (e) { if ((e as DOMException)?.name === 'AbortError') return null; }
  }
  await navigator.clipboard.writeText(link.url);
  return 'copied';
}

export function InviteLinkBox({ link, invitationId, onRegenerate, regenerating }: {
  link: InviteLink; invitationId: string; onRegenerate?: () => void; regenerating?: boolean;
}) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const record = (event: InviteLinkEvent) => { void api.invitationLinkEvent(ws!, invitationId, event).catch(() => undefined); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); record('copied'); toast('Invite link copied', 'success'); }
    catch { toast('Couldn’t copy. Select the link and copy it instead.', 'error'); }
  };
  const share = async () => {
    try {
      const done = await shareOrCopy(link);
      if (done) { record(done); if (done === 'copied') toast('Sharing isn’t available here, so the link was copied', 'success'); }
    } catch { toast('Couldn’t share or copy. Select the link and copy it instead.', 'error'); }
  };
  return (
    <div className="space-y-2" data-testid="invite-link">
      <div className="flex flex-wrap items-center gap-1.5 sm:flex-nowrap">
        <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto"><Input readOnly value={link.url} aria-label={`Invite link for ${link.for_name}`} onFocus={e => e.currentTarget.select()}
          className="font-mono text-xs" data-testid="invite-link-url" data-sensitive="invite-link" autoComplete="off" spellCheck={false} /></div>
        <Button size="sm" variant="secondary" onClick={() => void copy()} data-testid="invite-link-copy"><Copy className="h-4 w-4" />Copy</Button>
        <Button size="sm" variant="secondary" onClick={() => void share()} data-testid="invite-link-share"><Share2 className="h-4 w-4" />Share…</Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
        <HoverBubble bubble={`${inviteLinkExpiryExact(link.expires_at)}. Works once: after they set a password it can’t be used again.`}>
          <span data-testid="invite-link-expiry" tabIndex={0}>{inviteLinkExpiry(link.expires_at)}</span>
        </HoverBubble>
        {onRegenerate && (
          <HoverBubble bubble="Makes a new link with a new expiry. This link, and the one in any email, stop working.">
            <button type="button" className="inline-flex items-center gap-1 underline-offset-2 hover:underline" onClick={onRegenerate} disabled={regenerating} data-testid="invite-link-regenerate">
              <RefreshCw className={`h-3 w-3 ${regenerating ? 'animate-spin' : ''}`} />Regenerate link
            </button>
          </HoverBubble>
        )}
      </div>
      <p className="flex items-start gap-1.5 rounded-md bg-orange-50 px-2 py-1.5 text-xs text-orange-900 dark:bg-orange-900/30 dark:text-orange-100" data-testid="invite-link-warning">
        <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0" />{inviteLinkWarning(link.for_name)}
      </p>
    </div>
  );
}

/** Ask, then make a new link (the old one stops working). Returns the new link, or null. */
export async function regenerateLink(ws: string, inv: Pick<MemberInvitation, 'id' | 'email' | 'display_name' | 'status'>, toast: (m: string, t?: 'success' | 'error' | 'info') => void): Promise<InviteLink | null> {
  const who = inv.display_name || inv.email;
  if (!await ask({
    title: `Make a new invite link for ${who}?`,
    body: inv.status === 'emailed' ? 'The current link stops working, including the one in the email you sent. Share the new link or resend the email.' : 'The current link, if any, stops working.',
    confirmLabel: 'Regenerate link', danger: false,
  })) return null;
  try {
    const r = await api.regenerateInvitationLink(ws, inv.id);
    void queryClient.invalidateQueries({ queryKey: ['invitations', ws] });
    if (!r.link) { toast(r.outcome === 'added' || r.outcome === 'already_member' ? `${inv.email} already has an account and was added` : r.message ?? 'No link was made', r.link ? 'success' : 'info'); return null; }
    toast('New link made. The old one no longer works.', 'success');
    return r.link;
  } catch (e) { toast(errorMessage(e), 'error'); return null; }
}

/** "Copy invite link" from a list, drawer or user page: fetches the link (audited) and shows it with Copy / Share. */
export function InviteLinkModal({ inv, onClose }: { inv: MemberInvitation | null; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [fresh, setFresh] = useState<InviteLink | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: ['invite-link', ws, inv?.id], enabled: Boolean(ws && inv?.has_link), retry: false,
    queryFn: () => api.invitationLink(ws!, inv!.id), staleTime: 0, gcTime: 0, // never kept in the cache after the dialog closes
  });
  const close = () => { setFresh(null); onClose(); };
  if (!inv) return null;
  const link = fresh ?? q.data ?? null;
  const regen = async () => { setBusy(true); try { const l = await regenerateLink(ws!, inv, toast); if (l) setFresh(l); } finally { setBusy(false); } };
  return (
    <Modal open onClose={close} title={`Invite link · ${inv.display_name || inv.email}`} footer={<Button variant="secondary" onClick={close}>Done</Button>}>
      {link ? <InviteLinkBox link={link} invitationId={inv.id} onRegenerate={() => void regen()} regenerating={busy} />
        : q.isLoading && inv.has_link ? <p className="text-sm text-slate-500">Loading…</p>
        : (
          <div className="space-y-2" data-testid="invite-link-none">
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {q.error ? errorMessage(q.error) : 'This link can’t be shown again: links made before 20 October 2026, or when it expired, aren’t kept.'}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300">Regenerate makes a new link you can copy or share. The old link, including the one in any email, stops working.</p>
            <Button size="sm" onClick={() => void regen()} loading={busy} data-testid="invite-link-regenerate"><RefreshCw className="h-4 w-4" />Regenerate link</Button>
          </div>
        )}
    </Modal>
  );
}

/** Compact row actions for an open invitation: Copy invite link, Regenerate link. */
export function InviteLinkActions({ inv, compact }: { inv: MemberInvitation; compact?: boolean }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [open, setOpen] = useState<MemberInvitation | null>(null);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<InviteLink | null>(null);
  const regen = async () => { setBusy(true); try { const l = await regenerateLink(ws!, inv, toast); if (l) setMade(l); } finally { setBusy(false); } };
  return <>
    <HoverBubble bubble={inv.has_link ? 'Show the link to copy or share it yourself. Viewing is recorded in the audit log.' : 'This link can’t be shown again. Regenerate it to get one you can share.'}>
      <Button size="sm" variant="ghost" onClick={() => (inv.has_link ? setOpen(inv) : void regen())} loading={busy && !inv.has_link} data-testid="invite-copy-link">
        {inv.has_link ? <><Link2 className="h-4 w-4" />{compact ? 'Link' : 'Copy invite link'}</> : <><KeyRound className="h-4 w-4" />{compact ? 'New link' : 'Regenerate link'}</>}
      </Button>
    </HoverBubble>
    <InviteLinkModal inv={open} onClose={() => setOpen(null)} />
    {made && (
      <Modal open onClose={() => setMade(null)} title={`New invite link · ${inv.display_name || inv.email}`} footer={<Button variant="secondary" onClick={() => setMade(null)}>Done</Button>}>
        <InviteLinkBox link={made} invitationId={inv.id} />
      </Modal>
    )}
  </>;
}

/** On a person's drawer or user page: their open invitation, if any, with the link actions. Admins only. */
export function PersonInvitation({ userId, email }: { userId: string; email?: string | null }) {
  const ws = useWorkspaceId();
  const can = useCan('admin.users.manage');
  const q = useQuery({ queryKey: ['invitations', ws, 'open'], queryFn: () => api.invitations(ws!, 'open'), enabled: Boolean(ws && can) });
  const inv = inviteFor(q.data, userId, email);
  const { send, busy } = useResendInvite();
  if (!can || !inv) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700" data-testid="person-invitation">
      <span className="min-w-[12rem] flex-1 text-slate-700 dark:text-slate-200">Not set up yet
        {/* t514u: a muted "Invited 3 days ago" and when the link stops working. */}
        <span className="ml-1 text-xs text-slate-500 dark:text-slate-400" data-testid="invited-ago">{invitedAgo(inv)}{inv.expires_at ? ` · ${inviteLinkExpiry(inv.expires_at).toLowerCase()}` : ''}</span>
      </span>
      <Button size="sm" variant="secondary" loading={busy} onClick={() => void send(userId)} data-testid="resend-invite"><Mail className="h-4 w-4" />{RESEND_INVITE_LABEL}</Button>
      <InviteLinkActions inv={inv} compact />
    </div>
  );
}
